import React, { useState, useEffect } from 'react';
import { studentService } from '../services/studentService';
import { StudentDetail } from '../types';

interface StudentCodeSearchPageProps {
  onBack: () => void;
  onStudentFound: (student: StudentDetail) => void;
}

interface ClassOption {
  class_code: string;
  class_name: string;
}

const StudentCodeSearchPage: React.FC<StudentCodeSearchPageProps> = ({
  onBack,
  onStudentFound,
}) => {
  // Form state
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  
  // Data state
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [sections, setSections] = useState<string[]>([]);
  const [students, setStudents] = useState<StudentDetail[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<StudentDetail[]>([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingSections, setLoadingSections] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [error, setError] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);

  // Load classes on mount
  useEffect(() => {
    const loadClasses = async () => {
      try {
        setLoadingClasses(true);
        const classesData = await studentService.getAllClasses();
        setClasses(classesData);
      } catch (err) {
        setError('Failed to load classes');
        console.error('Error loading classes:', err);
      } finally {
        setLoadingClasses(false);
      }
    };
    loadClasses();
  }, []);

  // Load sections when class changes
  useEffect(() => {
    if (selectedClass) {
      const loadSections = async () => {
        try {
          setLoadingSections(true);
          setSections([]);
          setSelectedSection('');
          setStudents([]);
          setFilteredStudents([]);
          setSelectedStudent('');
          setStudentSearchQuery('');
          
          const sectionsData = await studentService.getSectionsByClass(selectedClass);
          setSections(sectionsData);
        } catch (err) {
          setError('Failed to load sections');
          console.error('Error loading sections:', err);
        } finally {
          setLoadingSections(false);
        }
      };
      loadSections();
    } else {
      setSections([]);
      setSelectedSection('');
      setStudents([]);
      setFilteredStudents([]);
      setSelectedStudent('');
      setStudentSearchQuery('');
    }
  }, [selectedClass]);

  // Load students when class and section are selected
  useEffect(() => {
    if (selectedClass && selectedSection) {
      const loadStudents = async () => {
        try {
          setLoadingStudents(true);
          setStudents([]);
          setFilteredStudents([]);
          setSelectedStudent('');
          setStudentSearchQuery('');
          
          const studentsData = await studentService.getStudentsByClass(selectedClass);
          // Filter by section
          const sectionStudents = studentsData.filter(
            s => s.class === selectedSection
          );
          setStudents(sectionStudents);
          setFilteredStudents(sectionStudents);
        } catch (err) {
          setError('Failed to load students');
          console.error('Error loading students:', err);
        } finally {
          setLoadingStudents(false);
        }
      };
      loadStudents();
    } else {
      setStudents([]);
      setFilteredStudents([]);
      setSelectedStudent('');
      setStudentSearchQuery('');
    }
  }, [selectedClass, selectedSection]);

  // Filter students based on search query
  useEffect(() => {
    if (studentSearchQuery.trim() === '') {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter(student =>
        student.studentName.toLowerCase().includes(studentSearchQuery.toLowerCase()) ||
        student.studentCode.toLowerCase().includes(studentSearchQuery.toLowerCase())
      );
      setFilteredStudents(filtered);
    }
  }, [studentSearchQuery, students]);

  const handleStudentSelect = (student: StudentDetail) => {
    setSelectedStudent(student.studentCode);
    setStudentSearchQuery(student.studentName);
    setShowStudentDropdown(false);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedClass) {
      setError('Please select a class');
      return;
    }
    
    if (!selectedSection) {
      setError('Please select a section');
      return;
    }
    
    if (!selectedStudent) {
      setError('Please select a student');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const student = await studentService.getStudentByCode(selectedStudent);
      
      if (student) {
        onStudentFound(student);
      } else {
        setError('Student not found. Please try again.');
      }
    } catch (err) {
      setError('An error occurred while searching. Please try again.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedClass('');
    setSelectedSection('');
    setSelectedStudent('');
    setStudentSearchQuery('');
    setError('');
    setSections([]);
    setStudents([]);
    setFilteredStudents([]);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:pb-32">
      <div className="w-full max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800">
              Search Student
            </h1>
            <p className="text-gray-600 mt-2">
              Select class, section and student name to view details
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="space-y-5">
            {/* Class Selection */}
            <div>
              <label
                htmlFor="class"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Class <span className="text-red-500">*</span>
              </label>
              <select
                id="class"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                disabled={loadingClasses || loading}
              >
                <option value="">
                  {loadingClasses ? 'Loading classes...' : 'Select a class'}
                </option>
                {classes.map((cls) => (
                  <option key={cls.class_code} value={cls.class_code}>
                    {cls.class_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Section Selection */}
            <div>
              <label
                htmlFor="section"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Section <span className="text-red-500">*</span>
              </label>
              <select
                id="section"
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100"
                disabled={!selectedClass || loadingSections || loading}
              >
                <option value="">
                  {loadingSections
                    ? 'Loading sections...'
                    : selectedClass
                    ? 'Select a section'
                    : 'Select a class first'}
                </option>
                {sections.map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </select>
            </div>

            {/* Student Name Searchable Dropdown */}
            <div className="relative">
              <label
                htmlFor="studentName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Student Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="studentName"
                value={studentSearchQuery}
                onChange={(e) => {
                  setStudentSearchQuery(e.target.value);
                  setShowStudentDropdown(true);
                  setSelectedStudent('');
                }}
                onFocus={() => setShowStudentDropdown(true)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-100"
                placeholder={
                  loadingStudents
                    ? 'Loading students...'
                    : !selectedClass || !selectedSection
                    ? 'Select class and section first'
                    : 'Search student by name or code'
                }
                disabled={!selectedClass || !selectedSection || loadingStudents || loading}
                autoComplete="off"
              />
              
              {/* Dropdown List */}
              {showStudentDropdown && selectedClass && selectedSection && !loadingStudents && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredStudents.length > 0 ? (
                    filteredStudents.map((student) => (
                      <div
                        key={student.studentCode}
                        onClick={() => handleStudentSelect(student)}
                        className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors"
                      >
                        <div className="font-medium text-gray-900">
                          {student.studentName}
                        </div>
                        <div className="text-sm text-gray-500">
                          Code: {student.studentCode} | Class: {student.standard} - {student.class}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-center">
                      {studentSearchQuery.trim() ? 'No students found' : 'Start typing to search'}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
                <svg
                  className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Searching...
                  </span>
                ) : (
                  'Search'
                )}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={loading}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Reset
              </button>
            </div>
          </form>
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How to Search</p>
              <p>
                Select class and section first, then search for student by name. You can type to filter the list.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Action Bar (Desktop only, floating on mobile) */}
      <div className="md:fixed md:bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl z-50 mt-8 md:mt-0">
        <div className="max-w-7xl mx-auto px-2 py-4">
          <div className="flex gap-4 max-w-2xl mx-auto">
            <button
              onClick={onBack}
              className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-4 px-6 rounded-lg transition-colors duration-200 text-lg"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentCodeSearchPage;
